// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import FollowingFeedManageUserGridRep from 'app/common/react/components/FollowingFeed/FollowingFeedManageUserGridRep';
import i18n from 'app/common/lib/i18n';
import PrettyNumber from 'app/common/react/i18n/PrettyNumber';
import TimeSpent from 'app/common/lib/react/TimeSpent';
import Toast from 'app/common/react/ui/Toast';
import type { Dispatch } from 'redux';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import type { SuggestionUser } from 'app/common/react/components/FollowingFeed/FollowingFeedTypes';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import withShowToast from 'app/common/lib/react/withShowToast';
import { compose } from 'redux';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { startTiming } from 'app/common/lib/TraceLogger/logTraceEvent';
import { Box, Container, Masonry, MasonryUniformRowLayout, Spinner, Text } from 'gestalt';
import { follow, unfollow } from 'app/common/redux/actions/user/follow';

const DESCRIPTION_HEIGHT = 36;
const ITEM_WIDTH = 365;
const MEASUREMENT_STORE = Masonry.createMeasurementStore();

export type Props = {
  isOwnProfile: boolean,
  limit?: number,
  scrollContainer?: ?HTMLElement,
  users: {
    data: Array<ProfileUser>,
    fetchMore: () => void,
    isAtEnd: boolean,
    isFetching: boolean,
    isLoaded: boolean,
  },
};

type UserCardProps = {
  data: ProfileUser,
  followUser: (string) => void,
  hideAllToasts: () => void,
  isFollowed: boolean,
  itemIdx: number,
  showToast: (Toast: Object) => void,
  unfollowUser: (string) => void,
  viewer: Viewer,
};

export function UserCard({
  data: user,
  followUser,
  hideAllToasts,
  isFollowed,
  unfollowUser,
  viewer,
  showToast,
  itemIdx,
}: UserCardProps) {
  const userUrl = `/${user.username}/`;

  const renderDescription = (user: SuggestionUser) => {
    const {
      follower_count: followerCount,
      recommendation_reason: recommendationReason,
      board_count: boardCount,
      pin_count: pinCount,
    } = user;
    return (
      <Box height={DESCRIPTION_HEIGHT} overflow="hidden">
        <Text size="sm" color="darkGray" truncate>
          <PrettyNumber value={followerCount} shortform shortformMaximumFractionDigits={1} />
          {i18n.ngettext(' Follower', ' Followers', followerCount, 'follower count on usercard')}
        </Text>
        <Text size="xs" color="gray" truncate>
          {recommendationReason ||
            interpolateNamedTemplate(
              i18n._(
                '{{ pinCount }} Pins {{ boardCount }} Boards',
                'Number of pins user has on their profile',
              ),
              {
                pinCount: (
                  <PrettyNumber value={pinCount} shortform shortformMaximumFractionDigits={1} />
                ),
                boardCount,
              },
            )}
        </Text>
      </Box>
    );
  };

  const onClick = isFollowed
    ? () => {
        unfollowUser(user.id);
      }
    : () => {
        followUser(user.id);
        hideAllToasts();
        showToast(({ onHide }) => (
          <Toast
            onClick={() => {
              startTiming(PCONST.thrift.TracingClientTypes.PWTAction.OTHER_PROFILE);
            }}
            onHide={onHide}
            text={[
              i18n._('Now following', 'text that comes before a user name in toast'),
              user.full_name,
            ]}
            href={userUrl}
            imageUrl={user.image_medium_url}
          />
        ));
      };

  return (
    <FollowingFeedManageUserGridRep
      key={user.id}
      user={user}
      onFollow={onClick}
      itemIdx={itemIdx}
      renderDescription={renderDescription}
    />
  );
}

const renderUser = compose(
  connect(
    (state, { isFollowed, data, itemIdx }) => {
      const user = state.users[data.id];
      return {
        isFollowed: user ? user.explicitly_followed_by_me : isFollowed,
        viewer: state.viewer,
        itemIdx,
      };
    },
    (dispatch: Dispatch<any>) => ({
      followUser: (id) => dispatch(follow(id)),
      unfollowUser: (id) => dispatch(unfollow(id)),
    }),
  ),
  withShowToast,
)(UserCard);

function ProfileCommunityUserGrid({ users, isOwnProfile, limit = 0, scrollContainer }: Props) {
  const { data, fetchMore, isFetching, isLoaded } = users;

  if (!isLoaded) {
    return null;
  }

  let filteredUsers = data && data.filter((user) => user.last_pin_save_time);
  let fetchMoreWithLimit = fetchMore;
  if (limit > 0 && filteredUsers) {
    filteredUsers = filteredUsers.slice(0, limit);
    fetchMoreWithLimit = () => {};
  }

  const timeSpentViewParam = isOwnProfile
    ? PCONST.thrift.ViewParameterType.USER_SELF
    : PCONST.thrift.ViewParameterType.USER_OTHERS;

  return (
    <TimeSpent
      view={PCONST.thrift.ViewType.USER}
      viewParameter={timeSpentViewParam}
      component={PCONST.thrift.ComponentType.USER_FOLLOWERS}
    >
      <Container>
        <Masonry
          comp={renderUser}
          columnWidth={ITEM_WIDTH}
          items={filteredUsers || []}
          loadItems={fetchMoreWithLimit}
          virtualize
          measurementStore={MEASUREMENT_STORE}
          scrollContainer={() => scrollContainer || window}
          minCols={1}
          layout={new MasonryUniformRowLayout()}
        />
        {isLoaded &&
          data.length === 0 && (
            <Box marginBottom={3}>
              <Spinner
                accessibilityLabel={i18n._(
                  'Loading additional people',
                  'Label for the loading state spinner in the People section(s) of the profile community page',
                )}
                show={isFetching || !isLoaded}
              />
            </Box>
          )}
      </Container>
    </TimeSpent>
  );
}

export default ProfileCommunityUserGrid;
